import React, { useState, useEffect } from 'react';
import api from '../api';
import { Plus, Lightning, Calendar, Clock, Camera, X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { uploadFile } from '../utils/uploadFile';

const SolicitarAtendimento = ({ clientId, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [services, setServices] = useState({});
  const [selectedServices, setSelectedServices] = useState([]);
  const [urgencia, setUrgencia] = useState('normal');
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [descricao, setDescricao] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchServices();
    }
  }, [isOpen]);

  const fetchServices = async () => {
    setLoadingServices(true);
    try {
      const res = await api.get('/services');
      // Group by category, only active services
      const grouped = {};
      res.data.filter(s => s.ativo !== false).forEach(s => {
        if (!grouped[s.categoria]) grouped[s.categoria] = [];
        grouped[s.categoria].push(s.nome);
      });
      setServices(grouped);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const toggleService = (service) => {
    if (selectedServices.includes(service)) {
      setSelectedServices(selectedServices.filter(s => s !== service));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const handleFotoSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024);
    setFotos(prev => [...prev, ...files.map(file => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const removeFoto = (idx) => {
    setFotos(prev => { const u = [...prev]; URL.revokeObjectURL(u[idx].preview); u.splice(idx, 1); return u; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedServices.length === 0) {
      alert('Selecione pelo menos um servico');
      return;
    }

    if (urgencia === 'programado' && !dataAgendamento) {
      alert('Selecione uma data para agendamento programado');
      return;
    }

    setLoading(true);

    try {
      // Upload photos
      const fotoUrls = [];
      for (let i = 0; i < fotos.length; i++) {
        setUploadProgress(Math.round(((i) / fotos.length) * 100));
        const url = await uploadFile(fotos[i].file, `solicitacoes/${clientId}`);
        fotoUrls.push(url);
      }
      if (fotos.length > 0) setUploadProgress(100);

      await api.post('/solicitacoes', {
        client_id: clientId,
        servicos_solicitados: selectedServices,
        descricao: descricao || null,
        urgencia,
        data_agendamento: dataAgendamento || null,
        fotos_problema: fotoUrls
      });

      fotos.forEach(f => URL.revokeObjectURL(f.preview));
      setIsOpen(false);
      setSelectedServices([]);
      setUrgencia('normal');
      setDataAgendamento('');
      setDescricao('');
      setFotos([]);
      setUploadProgress(0);
      
      if (onSuccess) onSuccess();
      alert('Solicitacao enviada com sucesso! Em breve um tecnico sera atribuido.');
    } catch (error) {
      console.error('Error creating solicitacao:', error);
      alert('Erro ao criar solicitacao: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getUrgenciaIcon = (nivel) => {
    switch(nivel) {
      case 'urgente': return <Lightning size={20} weight="fill" className="text-red-500" />;
      case 'rapido': return <Clock size={20} className="text-orange-500" />;
      case 'programado': return <Calendar size={20} className="text-blue-500" />;
      default: return <Clock size={20} className="text-zinc-400" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="solicitar-atendimento-button">
          <Plus size={20} className="mr-2" />
          Solicitar Atendimento
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
            Solicitar Atendimento
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Urgencia */}
          <div>
            <Label className="mb-3 block">Nivel de Urgencia *</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'urgente', label: 'URGENTE', desc: 'Atendimento imediato', color: 'border-red-500' },
                { value: 'rapido', label: 'Rapido', desc: 'Dentro de 24h', color: 'border-orange-500' },
                { value: 'normal', label: 'Normal', desc: 'Ate 3 dias', color: 'border-blue-500' },
                { value: 'programado', label: 'Programado', desc: 'Agendar data', color: 'border-green-500' }
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setUrgencia(option.value)}
                  className={`p-4 border-2 rounded-sm transition-all ${
                    urgencia === option.value
                      ? `${option.color} bg-[#1E1E1E]`
                      : 'border-[#27272A] hover:border-[#3A3A3A]'
                  }`}
                  data-testid={`urgencia-${option.value}`}
                >
                  <div className="flex items-center justify-center mb-2">
                    {getUrgenciaIcon(option.value)}
                  </div>
                  <div className="font-semibold">{option.label}</div>
                  <div className="text-xs text-zinc-400 mt-1">{option.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Data de Agendamento */}
          {urgencia === 'programado' && (
            <div>
              <Label>Data Desejada *</Label>
              <input
                type="date"
                value={dataAgendamento}
                onChange={(e) => setDataAgendamento(e.target.value)}
                className="w-full px-4 py-2 bg-[#050505] border border-[#27272A] rounded-sm text-white focus:border-[#FFB800] focus:ring-1 focus:ring-[#FFB800] outline-none"
                min={new Date().toISOString().split('T')[0]}
                required
                data-testid="data-agendamento-input"
              />
            </div>
          )}

          {/* Servicos */}
          <div>
            <Label className="mb-3 block">Servicos Necessarios * (selecione ao menos um)</Label>
            {loadingServices ? (
              <div className="text-center py-4 text-zinc-400">Carregando servicos...</div>
            ) : Object.keys(services).length === 0 ? (
              <div className="text-center py-4 text-zinc-400">Nenhum servico disponivel</div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto bg-[#050505] border border-[#27272A] rounded-sm p-4">
                {Object.entries(services).sort(([a], [b]) => a.localeCompare(b)).map(([category, svcList]) => (
                  <div key={category}>
                    <h4 className="text-[#FFB800] font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      {category}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {svcList.map(service => (
                        <div key={service} className="flex items-center space-x-2">
                          <Checkbox
                            id={service}
                            checked={selectedServices.includes(service)}
                            onCheckedChange={() => toggleService(service)}
                            className="border-[#27272A] data-[state=checked]:bg-[#FFB800] data-[state=checked]:border-[#FFB800]"
                          />
                          <label htmlFor={service} className="text-sm text-zinc-400 cursor-pointer">
                            {service}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedServices.length > 0 && (
              <div className="mt-2 text-sm text-[#FFB800]">
                {selectedServices.length} servico(s) selecionado(s)
              </div>
            )}
          </div>

          {/* Descricao */}
          <div>
            <Label>Descricao Adicional (opcional)</Label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              className="w-full px-4 py-2 bg-[#050505] border border-[#27272A] rounded-sm text-white focus:border-[#FFB800] focus:ring-1 focus:ring-[#FFB800] outline-none min-h-24"
              placeholder="Descreva mais detalhes sobre o problema ou servico necessario..."
              data-testid="descricao-textarea"
            />
          </div>

          {/* Fotos do Problema */}
          <div>
            <Label className="mb-2 block">Fotos do Problema (opcional)</Label>
            <div className="bg-[#050505] border border-[#27272A] rounded-sm p-4">
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#27272A] rounded-sm p-4 cursor-pointer hover:border-[#FFB800] transition-colors" data-testid="foto-problema-upload">
                <Camera size={28} className="text-zinc-500 mb-1" />
                <span className="text-sm text-zinc-400">Anexe fotos do problema</span>
                <span className="text-xs text-zinc-600">JPG, PNG - max 10MB</span>
                <input type="file" accept="image/*" multiple onChange={handleFotoSelect} className="hidden" />
              </label>
              {fotos.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {fotos.map((f, idx) => (
                    <div key={idx} className="relative group">
                      <img src={f.preview} alt={`Foto ${idx+1}`} className="w-full h-20 object-cover rounded-sm border border-[#27272A]" />
                      <button type="button" onClick={() => removeFoto(idx)} className="absolute top-1 right-1 bg-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} className="text-white" /></button>
                    </div>
                  ))}
                </div>
              )}
              {loading && fotos.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-zinc-400 mb-1">Enviando fotos... {uploadProgress}%</div>
                  <div className="w-full bg-[#27272A] rounded-full h-1.5"><div className="bg-[#FFB800] h-1.5 rounded-full" style={{width:`${uploadProgress}%`}} /></div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 bg-[#1E1E1E] text-white hover:bg-[#27272A]"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#FFB800] text-black hover:bg-[#E6A600]"
              data-testid="submit-solicitacao-button"
            >
              {loading ? 'Enviando...' : 'Enviar Solicitacao'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SolicitarAtendimento;
